// frontend/src/utils/indexHtmlUrl.js
//
// The ONE rule for the `/<route>/index.html` address the static host hands us.
//
// The GCS bucket behind the load balancer 301-redirects a no-slash hard load of
// a prerendered route (`/register?s=k8`, `/login`, `/terms`, profession and
// blog pages) to `/<route>/index.html`, query kept. React Router has no route
// for that path, and every analytics lane reads the router's location - so if
// the app boots on it, `<Routes>` matches nothing for a moment and page views
// are counted on a path that does not exist (then counted again on the real
// one).
//
// `normalizeIndexHtmlLocation` rewrites the address bar ONCE, synchronously,
// before React mounts (src/index.js), so the router, CaptureAcquisition and
// every analytics lane start from the clean URL. `RedirectIndexHtml` reuses
// `stripIndexHtml` as the belt for an in-app navigation to such a path, and
// AnalyticsContext uses `isIndexHtmlPath` to send nothing while one is shown.
//
// The clean form KEEPS the trailing slash (`/register/`): that is the URL the
// bucket serves at 200 and the canonical form (utils/canonicalUrl.js).
// Stripping the slash too would re-trigger the 301 on the next reload.
//
// Pure apart from `normalizeIndexHtmlLocation`, which touches only the window
// it is given. Nothing here logs: the address can carry acquisition codes and
// friend codes, and none of it belongs in a console line.

/** The page name the host appends; matched exactly (the host emits it lowercase). */
const INDEX_HTML_SUFFIX = "/index.html";

/**
 * True when `pathname` is a `/<route>/index.html` (or bare `/index.html`)
 * address. Only an exact trailing `/index.html` segment counts:
 * `/x/index.html.bak`, `/index.htmlx` and `/a/index.html/b` do not.
 *
 * @param {string} pathname A location pathname (no query, no hash).
 * @returns {boolean}
 */
export const isIndexHtmlPath = (pathname) =>
  typeof pathname === "string" && pathname.endsWith(INDEX_HTML_SUFFIX);

/**
 * `pathname` with a trailing `index.html` segment removed, trailing slash kept:
 * `/register/index.html` -> `/register/`, `/index.html` -> `/`. Leading
 * slashes are collapsed to one (`//register/index.html` -> `/register/`).
 * Any other value (including a non-string) is returned unchanged.
 *
 * Why the collapse: `https://leadifyai.biz//register/index.html` is a valid
 * same-origin address whose pathname starts with `//`, and `//register/` used
 * as a URL is PROTOCOL-RELATIVE - host "register". history.replaceState
 * refuses that with a SecurityError, which would break the rewrite here and
 * crash RedirectIndexHtml's navigate().
 *
 * @param {string} pathname A location pathname (no query, no hash).
 * @returns {string}
 */
export const stripIndexHtml = (pathname) => {
  if (!isIndexHtmlPath(pathname)) return pathname;
  // Drop "index.html" but keep the "/" in front of it.
  const stripped = pathname.slice(0, pathname.length - INDEX_HTML_SUFFIX.length + 1);
  return stripped.replace(/^\/{2,}/, "/");
};

/**
 * Rewrite the current address from `/<route>/index.html` to `/<route>/` in
 * place (history.replaceState - no navigation, no new history entry), keeping
 * the query string, the hash and `history.state` exactly as they were.
 *
 * Call once at boot, BEFORE React mounts. Never throws: a window whose
 * location or history cannot be read or written (sandboxed frame, hostile
 * environment) is left as it is, and RedirectIndexHtml still corrects the route
 * after mount.
 *
 * @param {Window} [win=window] The window to normalise (injectable for tests).
 * @returns {boolean} True only when the address was actually rewritten.
 */
export const normalizeIndexHtmlLocation = (win = window) => {
  try {
    const { pathname, search, hash } = win.location;
    if (!isIndexHtmlPath(pathname)) return false;
    win.history.replaceState(
      win.history.state,
      "",
      `${stripIndexHtml(pathname)}${search}${hash}`
    );
    return true;
  } catch (e) {
    // Never break boot over an address-bar cosmetic: RedirectIndexHtml and the
    // analytics guards still cover this visit.
    return false;
  }
};
